import { AdminInput, Box, Div, Icon, TrashIconRed } from '@vgrubs/components';
import {
  memberKey,
  type AudienceGroup,
  type MediaKind,
} from '../helpers/announcementAudience';
import {
  ChipRow,
  GroupImageColumn,
  GroupLayout,
  ImageRequiredHint,
  TargetChip,
} from './AnnouncementGroupCard.styled';
import { AnnouncementMediaInput } from './AnnouncementMediaInput';

type Props = {
  group: AudienceGroup;
  onName: (name: string) => void;
  onMedia: (media: File | Blob | string | null, kind: MediaKind) => void;
  onMediaKind: (kind: MediaKind) => void;
  onMinVersion: (minAppVersion: string) => void;
  /** Index into `group.chips`, which is the order they are rendered in. */
  onToggleChip: (index: number) => void;
  onRemove: () => void;
  /** At least one member here would be saved without an image or GIF. */
  missingMedia: boolean;
};

export const AnnouncementGroupCard = ({
  group,
  onName,
  onMedia,
  onMediaKind,
  onMinVersion,
  onToggleChip,
  onRemove,
  missingMedia,
}: Props) => (
  /* Flat inside the wizard: the modal is already a raised surface, and a
     stack of shadowed cards on top of it reads as three separate dialogs. */
  <Box $boxNoShadow $p="20px">
    <GroupLayout>
      <Div $width="100%">
        <Div $flex $alignCenter $gap="8px" $mb="10">
          <AdminInput
            label=""
            placeholder="Group name"
            value={group.name}
            onChange={(e) => onName(e.target.value)}
          />
          <Icon $pointer onClick={onRemove}>
            <TrashIconRed />
          </Icon>
        </Div>

        <GroupImageColumn>
          <AnnouncementMediaInput
            kind={group.mediaKind}
            media={group.media}
            storedMedia={group.storedMedia}
            onKind={onMediaKind}
            onChange={onMedia}
          />
          {missingMedia && (
            <ImageRequiredHint>
              An image or a GIF is required for every selected target. Pick one
              for this group.
            </ImageRequiredHint>
          )}
        </GroupImageColumn>

        <ChipRow>
          {group.chips.map((chip, index) => (
            <TargetChip
              key={memberKey(chip)}
              type="button"
              $on={chip.on}
              onClick={() => onToggleChip(index)}
            >
              {chip.label}
            </TargetChip>
          ))}
        </ChipRow>

        <AdminInput
          label="Min app version (optional)"
          placeholder="e.g. 3.2.0"
          value={group.minAppVersion}
          onChange={(e) => onMinVersion(e.target.value)}
        />
      </Div>
    </GroupLayout>
  </Box>
);
